import { useEffect, useMemo, useState } from "react";
import { CaretDown, CheckCircle, LinkSimple, Minus, Plus, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { errMsg } from "@/lib/api";
import { ConfirmationStamp } from "@/components/common";
import {
  BREAKDOWN_KEYS,
  DIETARY_TALLY_KEYS,
  deriveBreakdown,
  deriveDietary,
} from "@/lib/guests";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const BLANK_NUMBERS = {
  adults: 0,
  teens: 0,
  children: 0,
  babies: 0,
  vegetarian: 0,
  vegan: 0,
  gluten_free: 0,
  dairy_free: 0,
  nut_allergy: 0,
  halal: 0,
  other_notes: "",
};

// Guest numbers editor shared by the client Guests page and the staff planning section.
// Counts are seeded from the named guest list and may go higher, never lower.
export function GuestNumbersPanel({
  stored,
  guests,
  locked = false,
  onSave,
  onUnlock,
  defaultOpen = true,
  heading = "Final guest count",
}) {
  const [numbers, setNumbers] = useState(BLANK_NUMBERS);
  const [open, setOpen] = useState(defaultOpen);
  const [saving, setSaving] = useState("");

  useEffect(() => {
    if (stored) {
      setNumbers({ ...BLANK_NUMBERS, ...stored });
    }
  }, [stored]);
  useEffect(() => {
    setOpen(defaultOpen);
  }, [defaultOpen]);

  const list = useMemo(() => guests || [], [guests]);
  const floors = useMemo(
    () => ({ ...deriveBreakdown(list), ...deriveDietary(list) }),
    [list],
  );
  const effective = useMemo(() => {
    const merged = { ...numbers };
    Object.entries(floors).forEach(([key, floor]) => {
      merged[key] = Math.max(Number(merged[key]) || 0, floor);
    });
    return merged;
  }, [numbers, floors]);

  const total = BREAKDOWN_KEYS.reduce((sum, key) => sum + (effective[key.id] || 0), 0);
  const dietaryTotal = DIETARY_TALLY_KEYS.reduce((sum, key) => sum + (effective[key.id] || 0), 0);
  const namedTotal = BREAKDOWN_KEYS.reduce((sum, key) => sum + (floors[key.id] || 0), 0);
  const extra = total - namedTotal;

  const step = (key, delta) => {
    if (locked) {
      return;
    }
    const floor = floors[key] || 0;
    setNumbers((current) => ({
      ...current,
      [key]: Math.min(500, Math.max(floor, (effective[key] || 0) + delta)),
    }));
  };

  const save = async (clientStatus) => {
    setSaving(clientStatus === "confirmed" ? "confirm" : "draft");
    try {
      await onSave({ ...effective, other_notes: numbers.other_notes || "" }, clientStatus);
      toast.success(clientStatus === "confirmed" ? "Guest numbers confirmed." : "Draft saved.");
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving("");
    }
  };

  return (
    <section className="rounded-lg border border-border bg-card" data-testid="guest-numbers-section">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-4 p-6 text-left"
        data-testid="guest-numbers-toggle"
        aria-expanded={open}
      >
        <span>
          <span className="eyebrow">{heading}</span>
          <span className="mt-1 block font-heading text-3xl text-primary" data-testid="guests-total">
            {total}
          </span>
          <span className="mt-1 block text-sm text-muted-foreground">
            Confirm 28 days before the event. {dietaryTotal} dietary{" "}
            {dietaryTotal === 1 ? "requirement" : "requirements"}.
          </span>
        </span>
        <CaretDown
          size={22}
          className={`shrink-0 text-muted-foreground transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="border-t border-border p-6">
          {list.length > 0 && (
            <div className="flex items-start gap-3 rounded-lg border border-secondary/40 bg-secondary/10 p-4" data-testid="guests-synced-banner">
              <LinkSimple size={18} className="mt-0.5 shrink-0 text-secondary" />
              <p className="text-sm text-primary">
                Seeded from the guest list · {list.length} named{" "}
                {list.length === 1 ? "guest" : "guests"}.
                <span className="mt-1 block text-xs text-muted-foreground">
                  Counts can go higher than the named list, but never lower than it.
                </span>
              </p>
            </div>
          )}
          {extra > 0 && list.length > 0 && (
            <div className="mt-3 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="guests-count-warning">
              <Warning size={18} className="mt-0.5 shrink-0 text-amber-600" />
              <p className="text-sm text-amber-900">
                The count is {extra} higher than the {namedTotal} named{" "}
                {namedTotal === 1 ? "guest" : "guests"} on the list.
                <span className="mt-1 block text-xs text-amber-800">
                  That's fine — we cater to the count. Add the remaining names when you have them.
                </span>
              </p>
            </div>
          )}

          <p className="eyebrow mt-6">Breakdown</p>
          <div className="mt-2">
            {BREAKDOWN_KEYS.map((row) => (
              <CounterRow
                key={row.id}
                testid={`guests-row-${row.id}`}
                label={row.label}
                sub={row.sub}
                value={effective[row.id] || 0}
                disabled={locked}
                atFloor={(effective[row.id] || 0) <= (floors[row.id] || 0)}
                prefix={`guests-${row.id}`}
                onStep={(delta) => step(row.id, delta)}
              />
            ))}
          </div>

          <div className="mt-8 flex items-baseline justify-between gap-3">
            <p className="eyebrow">Dietary requirements</p>
            <p className="text-xs uppercase tracking-wide text-secondary" data-testid="guests-dietary-total">
              {dietaryTotal} guest{dietaryTotal === 1 ? "" : "s"}
            </p>
          </div>
          <p className="mt-2 text-sm text-muted-foreground">
            Tally guests with specific dietary needs. Our chef accommodates all requirements once
            final numbers are confirmed.
          </p>
          <div className="mt-2">
            {DIETARY_TALLY_KEYS.map((row) => (
              <CounterRow
                key={row.id}
                testid={`dietary-row-${row.id}`}
                label={row.label}
                sub={row.sub}
                value={effective[row.id] || 0}
                disabled={locked}
                atFloor={(effective[row.id] || 0) <= (floors[row.id] || 0)}
                prefix={`dietary-${row.id}`}
                onStep={(delta) => step(row.id, delta)}
              />
            ))}
          </div>

          <div className="py-5" data-testid="dietary-other-wrap">
            <p className="font-heading text-lg text-primary">Other</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Anything else? (e.g. FODMAP, shellfish allergy, religious requirements)
            </p>
            <Textarea
              className="mt-3"
              rows={2}
              value={numbers.other_notes || ""}
              disabled={locked}
              onChange={(event) => setNumbers({ ...numbers, other_notes: event.target.value })}
              placeholder="Describe any additional requirements…"
              data-testid="dietary-other-input"
            />
          </div>

          {locked ? (
            <div className="flex flex-wrap items-center gap-3 border-t border-border pt-5" data-testid="guests-locked-footer">
              <span className="flex items-center gap-2 text-sm text-emerald-700">
                <CheckCircle size={18} weight="fill" />
                Guest numbers confirmed
              </span>
              <ConfirmationStamp
                label="Confirmed"
                at={stored?.confirmed_at}
                by={stored?.confirmed_by}
                testid="guest-numbers-confirmed-stamp"
              />
              {onUnlock && (
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full"
                  onClick={onUnlock}
                  data-testid="staff-unlock-guest-numbers"
                >
                  Unlock for client edits
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3 border-t border-border pt-5">
              <Button
                variant="outline"
                className="rounded-full"
                disabled={!!saving}
                onClick={() => save("draft")}
                data-testid="guests-save-btn"
              >
                {saving === "draft" ? "Saving…" : "Save draft"}
              </Button>
              <Button
                className="rounded-full bg-primary hover:bg-primary/90"
                disabled={!!saving}
                onClick={() => save("confirmed")}
                data-testid="guests-confirm-btn"
              >
                {saving === "confirm" ? "Confirming…" : "Confirm numbers"}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function CounterRow({ label, sub, value, onStep, disabled, atFloor, testid, prefix }) {
  return (
    <div className="flex items-center gap-4 border-b border-border py-4" data-testid={testid}>
      <div className="min-w-0 flex-1">
        <p className="font-heading text-lg text-primary">{label}</p>
        <p className="mt-0.5 text-xs uppercase tracking-wide text-muted-foreground">{sub}</p>
      </div>
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onStep(-1)}
          disabled={disabled || atFloor}
          title={atFloor && !disabled ? "Can't go below the named guest list" : undefined}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary transition-colors duration-200 hover:bg-muted disabled:opacity-35"
          data-testid={`${prefix}-dec`}
          aria-label={`Decrease ${label}`}
        >
          <Minus size={16} />
        </button>
        <span className="min-w-9 text-center font-heading text-xl text-primary" data-testid={`${prefix}-count`}>
          {value}
        </span>
        <button
          type="button"
          onClick={() => onStep(1)}
          disabled={disabled}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-primary transition-colors duration-200 hover:bg-muted disabled:opacity-35"
          data-testid={`${prefix}-inc`}
          aria-label={`Increase ${label}`}
        >
          <Plus size={16} />
        </button>
      </div>
    </div>
  );
}
